import React from 'react';
import purify from 'dompurify';
import htmlReactParser, {
  domToReact,
  type DOMNode,
  type Element,
} from 'html-react-parser';
import _ from 'lodash';
import { BrightbackText } from '@bbkAdminUtils/brightback-expression';

type Props = {
  value: unknown;
  context?: Record<string, unknown>;
  allowedTags?: string[];
  allowedAttr?: string[];
  openLinksInNewTab?: boolean;
};

const CustomerHtml: React.FC<Props> = (props) => {
  const {
    value,
    context,
    allowedTags = [],
    allowedAttr = [],
    openLinksInNewTab = false,
  } = props;
  const isDebug = window.bbkClientConfig && !window.bbkClientConfig.is_prod;

  if (React.isValidElement(value)) {
    // Passed in non-customer html: react components instead. just render them.
    return value;
  }
  const resolvedValue = new BrightbackText(value as string).process(
    context || {},
    {
      errorFallback: isDebug ? '[ERROR]' : '',
    }
  );
  const cleanValue = purify.sanitize(resolvedValue, {
    ALLOWED_TAGS: ['em', 'div', 'strong', 'u', 'span', 'a', ...allowedTags],
    ALLOWED_ATTR: ['class', 'href', 'src', ...allowedAttr],
  });

  let useValue = cleanValue;
  if (!_.isString(cleanValue)) {
    useValue = '';
    console.error(
      'Trying to pass a non-string value to the htmlReactParser. Please check your values.',
      value,
      context,
      resolvedValue,
      cleanValue
    );
  }

  if (!openLinksInNewTab) {
    return htmlReactParser(useValue);
  }

  // openLinksInNewTab prop -> opens anchor tag links in a new and fresh tab
  return htmlReactParser(useValue, {
    replace: (node: DOMNode) => {
      const el = node as Element;
      if (el.type === 'tag' && el.name === 'a') {
        const { class: className, ...restAttribs } = el.attribs;
        return (
          <a
            {...restAttribs}
            className={className}
            target="_blank"
            rel="noopener noreferrer"
          >
            {domToReact(el.children as DOMNode[])}
          </a>
        );
      }
    },
  });
};

export default CustomerHtml;
